import React, { useRef } from 'react'

import {
  AlignItems,
  Body1,
  BorderStyle,
  Box,
  Button,
  ButtonColorVariant,
  ButtonSize,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  Heading3,
  Icon,
  IconVariant,
  JustifyContent,
  Overflow,
} from '@gousto-internal/citrus-react'

import { useHeightCollapseTransition } from '../useHeightCollapseTransition'
import { DiscountBadge } from './DiscountBadge'

export type CollapsedProps = {
  boxSize: string
  deliveryDay: string
  address: string
  isExpanded: boolean
  onEditClick: () => void
  discountText?: string
}

const InfoText = ({ value }: { value: string }) => (
  <Body1 size={[2, null, 6]} color={Color.ColdGrey_800}>
    {value}
  </Body1>
)

const InfoBullet = () => (
  <Body1 size={[2, null, 6]} color={Color.ColdGrey_800} aria-hidden>
    &bull;
  </Body1>
)

type EditButtonProps = {
  isExpanded: boolean
  onClick: () => void
  colorVariant: ButtonColorVariant
  hideLabel?: boolean
}

// Owns its transition so a pill arriving after the panel collapsed mounts open, not clipped.
const MobileDiscountBadge = ({ text, isExpanded }: { text: string; isExpanded: boolean }) => {
  const contentRef = useRef<HTMLDivElement>(null)
  // offsetHeight: the centred badge under-reports scrollHeight.
  const transition = useHeightCollapseTransition(contentRef, !isExpanded, (el) => el.offsetHeight)

  return (
    <Box
      display={[Display.Flex, null, Display.None]}
      alignItems={AlignItems.Center}
      overflowY={Overflow.Hidden}
      style={transition}
      aria-hidden={isExpanded || undefined}
      data-testid="collapsed-mobile-badge"
    >
      <Box ref={contentRef} display={Display.Flex}>
        <DiscountBadge text={text} />
      </Box>
    </Box>
  )
}

const EditButton = ({ isExpanded, onClick, colorVariant, hideLabel }: EditButtonProps) => {
  const label = isExpanded ? 'Close' : 'Edit'
  const ariaLabel = isExpanded ? 'Close delivery settings' : 'Edit delivery settings'
  const icon = isExpanded ? 'chevron_up' : 'chevron_down'

  return (
    <Button
      size={ButtonSize.Medium}
      colorVariant={colorVariant}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-expanded={isExpanded}
    >
      <Box display={Display.Flex} alignItems={AlignItems.Center} gap={1}>
        {!hideLabel && <Box>{label}</Box>}
        <Icon name={icon} size={6} variant={IconVariant.Inherit} />
      </Box>
    </Button>
  )
}

export const Collapsed = ({
  boxSize,
  deliveryDay,
  address,
  isExpanded,
  onEditClick,
  discountText,
}: CollapsedProps) => {
  const summaryRef = useRef<HTMLDivElement>(null)
  // Animates shut in step with the expanded panel opening, rather than vanishing
  // instantly and popping the bar's height (mobile stacks it under the heading).
  const summaryTransition = useHeightCollapseTransition(summaryRef, !isExpanded)

  return (
    <Box
      bg={Color.White}
      borderStyle={BorderStyle.Solid}
      borderColor={Color.ColdGrey_100}
      borderBottomWidth={0.5}
    >
      <Box
        maxWidth="1600px"
        paddingV={3}
        paddingH={[4, null, 8]}
        display={Display.Flex}
        alignItems={AlignItems.Center}
        justifyContent={JustifyContent.SpaceBetween}
        gap={[2, null, 3]}
        style={{ margin: '0 auto' }}
      >
        <Box
          display={Display.Flex}
          flexDirection={[FlexDirection.Column, null, FlexDirection.Row]}
          alignItems={[AlignItems.FlexStart, null, AlignItems.Baseline]}
          columnGap={3}
          // Zeroed when expanded — otherwise this flex gap leaves a blank strip below the
          // heading on mobile even though the summary row itself has collapsed to 0 height.
          rowGap={isExpanded ? 0 : 1}
          wrap
        >
          <Box display={Display.Flex} alignItems={AlignItems.Center} gap={2}>
            <Heading3 size={[4, null, 7]} fontWeight={FontWeight.Bold} color={Color.ColdGrey_800}>
              Your one-off box
            </Heading3>
            {discountText && <MobileDiscountBadge text={discountText} isExpanded={isExpanded} />}
          </Box>
          <Box
            ref={summaryRef}
            overflowY={Overflow.Hidden}
            style={summaryTransition}
            data-testid="collapsed-summary"
            aria-hidden={isExpanded || undefined}
          >
            <Box display={Display.Flex} alignItems={AlignItems.Center} gap={3} wrap>
              <Box display={Display.Flex} gap={1} alignItems={AlignItems.Baseline}>
                <InfoText value={deliveryDay} />
                <InfoBullet />
                <InfoText value={boxSize} />
                <InfoBullet />
                <InfoText value={address} />
              </Box>
              {discountText && (
                <Box display={[Display.None, null, Display.Flex]}>
                  <DiscountBadge text={discountText} />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <Box display={[Display.Flex, null, Display.None]}>
          <EditButton
            isExpanded={isExpanded}
            onClick={onEditClick}
            colorVariant={ButtonColorVariant.Tertiary}
            hideLabel
          />
        </Box>
        <Box display={[Display.None, null, Display.Flex]}>
          <EditButton
            isExpanded={isExpanded}
            onClick={onEditClick}
            colorVariant={ButtonColorVariant.Primary}
          />
        </Box>
      </Box>
    </Box>
  )
}
